import { ApiIndividualResponse } from './api'

// TODO: rename types of Mariano's api to use the AgentApi prefix
type PlaybookDefaultItems = {
  playbook_id: string
  title: string
  description?: string
  status: 'draft' | 'published' | 'archived' | string
  is_public: boolean
  created_at: string
  updated_at: string
  created_by: string
  active_version_id: string
  current_version: string
  total_versions: number
  collaborators?: Record<string, 'edit' | 'view'>
  workspace_access?: Record<string, unknown>
  output_type?: PlaybookOutputType
  category?: string
  conversation_context?: ConversationContext
  current_iteration?: IterationExtended
  training_videos?: PlaybookTrainingVideos
}

export enum PlaybookOutputType {
  Analysis = 'analysis', // @deprecated
  Playbook = 'playbook',
  ToolDiscovery = 'tool_discovery',
  Empty = 'empty',
}

export type Playbook = PlaybookEmpty | PlaybookWithSteps | PlaybookWithAnalysis

export type PlaybookEmpty = PlaybookDefaultItems & {
  output_type?: PlaybookOutputType.Empty
  playbook_data?: PlaybookEmptyData
}

export type PlaybookWithSteps = PlaybookDefaultItems & {
  playbook_data?: PlaybookWithStepsData
  output_type?: PlaybookOutputType.Playbook
}

export type PlaybookWithAnalysis = PlaybookDefaultItems & {
  playbook_data?: PlaybookWithAnalysisData
  output_type?: PlaybookOutputType.Analysis | PlaybookOutputType.ToolDiscovery
}

export type PlaybookDefaultData = {
  title: string
  success_metrics?: string[]
  objective: string
  ai_advantage: string
  tools_needed?: ToolLinkWithFavicon[]
}

type MermaidWorkflowInfo = {
  code: string
  type: string
}

export type PlaybookWithStepsData = PlaybookDefaultData & {
  mermaid_workflow?: MermaidWorkflowInfo
  steps: PlaybookStep[]
  output_type?: PlaybookOutputType.Playbook
}

export type PlaybookWithAnalysisData = PlaybookDefaultData & {
  analysis_content: string // @deprecated
  analysis: string
  /** @deprecated use tools_evaluated instead */
  items?: ToolLinkWithFavicon[] | ExtendedToolLinkWithFavicon[]
  tools_evaluated?: ToolLinkWithFavicon[] | ExtendedToolLinkWithFavicon[]
  top_pick?: Pick<ToolLinkWithFavicon, 'name'>
  output_type?: PlaybookOutputType.Analysis | PlaybookOutputType.ToolDiscovery
}

export type PlaybookEmptyData = PlaybookDefaultData & {
  output_type?: PlaybookOutputType.Empty
}

export enum PlaybookStepStatus {
  New = 'new',
  InProgress = 'in_progress',
  Complete = 'complete',
}

export type PlaybookStep = {
  title: string
  content: string
  prompt?: string
  tools?: ToolLinkWithFavicon[]
  status: PlaybookStepStatus
  updated_at: string // ISO date string
  updated_by: string // Display name, e.g., 'AI Assistant'
  updated_by_id: string // e.g., 'ai' or user id
  last_modified_by_ai_at?: string // ISO date string
}

export type ToolLink = {
  name: string
  url: string
}

export type ToolLinkWithFavicon = ToolLink & {
  favicon_url?: string | null
}

export type ExtendedToolLinkWithFavicon = ToolLinkWithFavicon & {
  type: 'primary' | 'secondary'
  best_use?: string
  best_for?: string[]
}

export type CodeSnippet = {
  code: string
  language?: string
  explanation?: string
}

export type TaskSimple = string

export type TaskDetailed = {
  task_number: number
  title: string
  description?: string
  estimated_time?: string
  difficulty?: 'beginner' | 'intermediate' | 'advanced' | string
  step_by_step_instructions?: string[]
  expected_outcome?: string
  required_tools?: string[]
  troubleshooting?: string[]
  resources?: string[]
  copy_paste_prompts?: string[]
  code_snippets?: CodeSnippet
}

export type PlaybookTask = TaskSimple | TaskDetailed

export type PlaybookTaskDetailed = TaskDetailed

export type ConversationContext = {
  current_agent: string
  last_action: string
  message_count: number
  requirements_completeness: number
  routing_history: string[]
  started_at: string
  started_by: string
}

export type Iteration = {
  current_agent: string
  started_at: string
  status: string
  user_message: string
  agent_updates: AgentUpdate[]
}

export type IterationExtended = Iteration & {
  completed_at?: string
}

export type AgentUpdate = {
  agent: string
  action: string
  message: string
  timestamp: string
  data?: Record<string, unknown>
}

export interface ApiPlaybookCreationRequest {
  title?: string
  description: string
  isPublic: boolean
}

export type ApiPlaybookUpdateRequest = Partial<ApiPlaybookCreationRequest>

export interface ApiPlaybookCreationResponse {
  _id: string
  status: string
}

export interface ApiPlaybookListItem {
  _id: string
  activeVersionId: string
  collaborators: Record<string, string>
  createdAt: string
  createdBy: string
  currentVersion: string
  description: string
  isPublic: boolean
  status: string
  title: string
  totalVersions: number
  updatedAt: string
  outputType: PlaybookOutputType
}

export interface ApiPlaybookCreationRequest {
  title?: string
  description: string
  isPublic: boolean
}

export interface ApiPlaybookCreationResponse {
  _id: string
  status: string
}

export interface ApiPlaybookListItem {
  _id: string
  activeVersionId: string
  collaborators: Record<string, string>
  createdAt: string
  createdBy: string
  currentVersion: string
  description: string
  isPublic: boolean
  status: string
  title: string
  totalVersions: number
  updatedAt: string
}

export interface Tool {
  name: string
  url: string
}

export enum ApiPlaybookStepStatus {
  Pending = 'pending',
  Complete = 'complete',
}

export type PlaybookUserAction =
  | 'rename'
  | 'copyLink'
  | 'duplicate'
  | 'versionHistory'
  | 'visibility'
  | 'delete'

export type PlaybookUserActionPayload<T extends PlaybookUserAction> =
  T extends 'rename'
    ? { playbook: Playbook; newTitle: string }
    : T extends 'visibility'
      ? { playbook: Playbook; visibility: 'public' | 'private' }
      : T extends 'delete'
        ? { playbook: Playbook }
        : T extends 'duplicate'
          ? { playbook: Playbook }
          : T extends 'versionHistory'
            ? { playbook: Playbook }
            : never

export interface ApiPlaybookCategory {
  _id: string
  description: string
  icon: string
  name: string
  playbookCount?: number
}

export interface PlaybookTrainingVideoItem {
  _id: string
  aiRelevanceScore: number
  relevanceReason: string
  thumbnailUrl: string
  title: string
  channelName: string
  url: string
  viewCount: number
  likeCount: number
}

export interface PlaybookTrainingVideos {
  keywords: string[]
  status: 'error' | 'generating' | 'not_generated' | 'ready'
  videos: PlaybookTrainingVideoItem[]
}

export interface AgentApiPlaybookTrainingVideoItem {
  ai_relevance_score: number
  channel_id: string
  channel_name: string
  comment_count: number
  description: string
  duration: string
  like_count: number
  published_at: string
  relevance_reason: string
  search_keyword: string
  thumbnail_url: string
  title: string
  url: string
  video_id: string
  view_count: number
}

export interface AgentApiPlaybookTrainingVideo {
  error: string | null
  generated_at: string
  keywords: string[]
  status: 'error' | 'generating' | 'not_generated' | 'ready'
  videos: AgentApiPlaybookTrainingVideoItem[]
}

export type PlaybookTrainingVideosMeta = Pick<
  PlaybookTrainingVideos,
  'keywords' | 'status'
>

export type PlaybookTrainingVideosPayload = ApiIndividualResponse<
  PlaybookTrainingVideoItem[],
  PlaybookTrainingVideosMeta
>

export enum PlaybookFeedbackType {
  Positive = 'positive',
  Negative = 'negative',
}

export interface PlaybookFeedback {
  _id: string
  playbookId: string
  playbookVersionId: string
  feedbackType: PlaybookFeedbackType
  userId: string
  comment?: string
  createdAt: string
  updatedAt: string
}

export type PlaybookFeedbackCreationPayload = Pick<
  PlaybookFeedback,
  'feedbackType' | 'comment'
>
